import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';

import { Layout } from './components/Layout';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import HomePage from './pages/HomePage';
import BulkPage from './pages/BulkPage';
import BankPage from './pages/BankPage';
import UitlegPage from './pages/UitlegPage';
import PricingPage from './pages/PricingPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ScanPage from './pages/ScanPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ExtensionPage from './pages/ExtensionPage';
import GeneratorPage from './pages/GeneratorPage';
import MT940Page from './pages/MT940Page';
import TeamPage from './pages/TeamPage';
import TeamJoinPage from './pages/TeamJoinPage';
import TaxTypePage from './pages/TaxTypePage';
import BtwCheckPage from './pages/BtwCheckPage';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const GuestRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppContent() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/bulk" element={<BulkPage />} />
        <Route path="/bank" element={<BankPage />} />
        <Route path="/uitleg" element={<UitlegPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/scan" element={<ScanPage />} />
        <Route path="/extension" element={<ExtensionPage />} />
        <Route path="/generator" element={<GeneratorPage />} />
        <Route path="/mt940" element={<MT940Page />} />
        <Route path="/btw-check" element={<BtwCheckPage />} />
        <Route 
          path="/login" 
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <GuestRoute>
              <ForgotPasswordPage />
            </GuestRoute>
          } 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPasswordPage />} 
        />
        <Route 
          path="/verify-email" 
          element={<VerifyEmailPage />} 
        />
        <Route 
          path="/payment/success" 
          element={<PaymentSuccessPage />} 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/team" 
          element={
            <ProtectedRoute>
              <TeamPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/team/join" 
          element={<TeamJoinPage />} 
        />
        <Route 
          path="/belastingtype/:taxType" 
          element={<TaxTypePage />} 
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router>
            <AppContent />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--bg-toast)',
                  color: 'var(--text-toast)',
                },
              }}
            />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
